import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as TabilityTypes from 'types';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';

// API
import * as remoteApi from 'api/remote';

// Components
import Loader from 'components/Loader';
import { AxiosResponse } from 'axios';
import { MapNode } from 'components/MapComponents';
import OutcomeMap from 'components/MapComponents/Outcome/OutcomeMap';

const LoadingContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

interface Props {
  plan: TabilityTypes.Plan;
}

function PlanContent(props: Props) {
  const { plan } = props;
  const [hasFetchedOutcomes, setHasFetchedOutcomes] = useState(false);

  const [initialTree, setInitialTree] = useState<Record<string, MapNode>>({});

  useEffect(() => {
    setHasFetchedOutcomes(false);
    setInitialTree({});
  }, [plan]);

  // Load the initial outcomes that we will use to populate the map
  const { isLoading: outcomesAreLoading } = useQuery(
    [queryKeys.outcomes, plan.nano_slug],
    remoteApi.fetchPlanOutcomes,
    {
      onSuccess: (response: AxiosResponse<TabilityTypes.Outcome[]>) => {
        const outcomes: TabilityTypes.Outcome[] = response.data;
        const newTree: Record<string, MapNode> = {
          plan: {
            id: 'plan',
            meta: { expanded: true, plan },
            type: 'smallPlan',
            name: plan.title,
            children: [],
            parents: [],
          },
        };
        let childNodes = outcomes.reduce(
          (acc, outcome) => {
            const planChildren = acc.plan ? acc.plan.children : [];
            const children = [...planChildren, `${outcome.id}`];
            const result = {
              ...acc,
              plan: {
                ...acc.plan,
                children,
              },
              [outcome.id]: {
                id: outcome.id,
                meta: { expanded: false, outcome: { ...outcome, plan } },
                type: 'outcome',
                name: outcome.title,
                children: [],
                parents: ['plan'],
              },
            };
            return result;
          },
          { ...newTree },
        );

        setInitialTree(childNodes);
        setHasFetchedOutcomes(true);
      },
    },
  );

  // Wait until plan and outcomes are loaded to display the plan map
  if (!plan || outcomesAreLoading) {
    return (
      <LoadingContainer>
        <Loader size="big" />
      </LoadingContainer>
    );
  }

  return (
    <>
      {hasFetchedOutcomes && (
        <ReactFlowProvider>
          <OutcomeMap initialTree={initialTree} rootId="plan" />
        </ReactFlowProvider>
      )}
    </>
  );
}

export default React.memo(PlanContent);
